import { ChangeEvent, useState } from "react";
import { send } from "src/utils/fetch";
import goto from "src/pageDefinitions/goto";
import {
  getInAppAutoCookie,
  setInAppAutoCookie,
} from "src/utils/authCookieParser";
import {
  saveMainSurveyAnswers,
  saveSurveyAnswers,
  setPurchaseSurveyAnswers,
  setHRTSurveyAnswers,
} from "src/utils/redux/slices/userData";
import { seedCurrentAnswers } from "src/utils/redux/slices/currentAnswers";
import getStore from "src/utils/redux/store";
import { useCallbackRef } from "src/hooks/lifecycle";
import { SelectedMedPlanType } from "src/utils/medPlanTypes";

import {
  getPayloadForGlp1CompanionUser,
  getPayloadForMedUser,
  getPayloadForMhtUser,
  getPayloadForHRTCheckout,
  getPayloadForHealthCheckUser,
  BodyGoalType,
} from "./purchaseUtils";
import {
  isB2BUpsellClinicalRoute,
  isFreemiumClinicalRoute,
} from "@utils/userSegment";
import { routeConstants } from "@utils/constants";
import { getRequestMetadata } from "src/utils/meristemContext";
import {
  BrowserSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import { setRecommendationContext } from "src/utils/redux/slices/recommendationContext";
import { setRecommendedPlanById } from "src/utils/redux/slices/recommendedPlan";
import {
  MED_FLOW,
  MED_SKU,
  NON_MED_PLAN,
  HealthPlan,
  getEligibleMedProducts,
  getEligibleMedProductsPlanSelection,
} from "src/utils/medEligibility";
import {
  getMedPlanIdBySKU,
  getSelectedMedPlanTypeByPlanId,
} from "src/utils/plans";
import { medSkuEntryPoints } from "@pageDefinitions/goto/paymentResults";

const isMedPlanType = (planType: string) => {
  return [
    "compounded",
    "compoundedTirzepatide",
    "branded",
    "brandedZepboundVial",
    "brandedWegovyPill",
    "oralsMetformin",
    "genericLiraglutide",
    "compoundedMicrodose",
    "compoundedMicrodoseProactiveHealthBiomarker",
    "compoundedMicrodoseTirzepatide",
  ].includes(planType);
};

const getMedSkuFromPlanType = (planType: string): MED_SKU => {
  switch (planType) {
    case "compounded":
      return MED_SKU.COMPOUNDED;
    case "compoundedTirzepatide":
      return MED_SKU.COMPOUNDED_TIRZEPATIDE;
    case "compoundedMicrodoseTirzepatide":
      return MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE;
    case "branded":
      return MED_SKU.BRANDED;
    case "brandedZepboundVial":
      return MED_SKU.BRANDED_ZEPBOUND_VIAL;
    case "brandedWegovyPill":
      return MED_SKU.BRANDED_WEGOVY_PILL;
    case "oralsMetformin":
      return MED_SKU.ORALS_METFORMIN;
    case "genericLiraglutide":
      return MED_SKU.GENERIC_LIRAGLUTIDE;
    case "compoundedMicrodose":
      return MED_SKU.COMPOUNDED_MICRODOSE;
    case "compoundedMicrodoseProactiveHealthBiomarker":
      return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
    case "cream":
      return MED_SKU.HRT_CREAM;
    case "patch":
      return MED_SKU.HRT_PATCH;
    default:
      return MED_SKU.BRANDED; // fallback
  }
};

export const redirectToCounterOffer = ({ subscriptionId }) => {
  let counterOfferRedirectURL = `${window.location.origin}/cancel?fcc=US&subscription_id=BMS_${subscriptionId}`;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("sha")) {
    counterOfferRedirectURL += `&sha=${urlParams.get("sha")}`;
  }

  window.location.href = counterOfferRedirectURL;
};

const prepareMedDataAndUrl = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
  useAsyncBrandedPlan = false,
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
  useAsyncBrandedPlan?: boolean;
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const isMed = isMedPlanType(planType);

  updateSessionState("browser", {
    selectedMedPlanType: undefined,
    selectedHRTPlanType: undefined,
  });

  if (isMed) {
    const data = glp1Companion
      ? getPayloadForGlp1CompanionUser(email)
      : getPayloadForMedUser(email, planType);

    // Force the branded async plan by seeding the survey answer that
    // `prefersBrandedCashpay()` reads from `userData.purchaseSurveyAnswers`.
    const paymentSurveyAnswers = useAsyncBrandedPlan
      ? {
          ...data.paymentSurveyAnswers,
          brandedCashPay: ["cashPay"] as ["cashPay"],
        }
      : data.paymentSurveyAnswers;

    await getStore().dispatch(
      saveSurveyAnswers(data.surveyAnswers, data.params)
    );
    await getStore().dispatch(setPurchaseSurveyAnswers(paymentSurveyAnswers));
    if (!glp1Companion) {
      let routeVal: string;
      if (isB2BUpsellClinicalRoute()) {
        routeVal = routeConstants.b2bUpsellClinical;
      } else if (isFreemiumClinicalRoute()) {
        routeVal = routeConstants.freemiumClinical;
      } else {
        routeVal = routeConstants.clinical;
      }

      urlParams.set("route", routeVal);
    }
  } else {
    urlParams.set("standaloneHRTBuyflowEnabledOverwrite", "true");
    urlParams.set("route", routeConstants.hrt);
    await setHRTPreCheckoutData({ email });
  }

  urlParams.set("fcc", "US");
  if (planType === "compoundedMicrodose") {
    urlParams.set("fsub", "OH");
  } else {
    urlParams.set("fsub", "AR");
  }
  if (isEmailUser) {
    urlParams.set("utm_source", "MedDrip");
  }

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);
};

export const redirectToTelehealth = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
  useAsyncBrandedPlan,
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
  useAsyncBrandedPlan?: boolean;
}) => {
  await prepareMedDataAndUrl({
    email,
    isEmailUser,
    glp1Companion,
    planType,
    useAsyncBrandedPlan,
  });

  const isMed = isMedPlanType(planType);

  if (isMed) {
    updateSessionState("browser", {
      selectedMedPlanType: planType as SelectedMedPlanType,
    });
  } else {
    updateSessionState("browser", {
      selectedHRTPlanType:
        planType as BrowserSessionState["selectedHRTPlanType"],
    });
  }

  goto.checkout("option_n_20221201_Ex1084var1", true);
};

/**
 * Scenario values accepted by getMockCoverageReportDataFromUrl
 * (CoverageReportScenario values plus the special "insuranceNotFound").
 */
export type InsuranceCoverageScenario =
  | "goodCoverage"
  | "poorCoverage"
  | "noCoverage"
  | "noBrandedEligible"
  | "medicareGovernmentInsurance"
  | "lowBMI"
  | "insuranceNotFound";

export const redirectToInsuranceCoverageReport = async ({
  email,
  coverageScenario,
  recommendedMedTier,
}: {
  email: string;
  coverageScenario: InsuranceCoverageScenario;
  /** When set, hard-forces the recommended plan on the coverage report. */
  recommendedMedTier?: MED_SKU;
}) => {
  // Seed med survey answers so getEligibleMedProducts() returns eligible tiers
  // (the coverage report redirects away when no recommended tier can be derived).
  // The display scenario is re-derived from eligibility (getCoverageScenario),
  // not taken from the URL, so eligibility-driven scenarios need matching answers:
  // - lowBMI: low-BMI profile so only the proactive-health tier is eligible.
  // - medicareGovernmentInsurance: hasMedicare fails the branded isNotMedicare
  //   check and drives the Medicare copy.
  // - noBrandedEligible: a branded-only ineligible health risk removes branded
  //   while keeping compounded tiers.
  const data = getPayloadForMedUser(
    email,
    coverageScenario === "lowBMI"
      ? "compoundedMicrodoseProactiveHealthBiomarker"
      : undefined
  );
  if (coverageScenario === "medicareGovernmentInsurance") {
    data.surveyAnswers = { ...data.surveyAnswers, hasMedicare: true };
  }
  if (coverageScenario === "noBrandedEligible") {
    data.surveyAnswers = {
      ...data.surveyAnswers,
      currentHealthRiskMedical: ["activeHeartDiseaseOrFailure"],
    };
  }
  await getStore().dispatch(saveSurveyAnswers(data.surveyAnswers, data.params));

  const urlParams = new URLSearchParams(window.location.search);
  if (coverageScenario === "medicareGovernmentInsurance") {
    urlParams.set("healthInsuranceMedicareOptionEnabledOverwrite", "true");
  } else {
    urlParams.delete("healthInsuranceMedicareOptionEnabledOverwrite");
  }
  urlParams.set("route", routeConstants.insuranceChecker);
  urlParams.set("fcc", "US");
  urlParams.set("fsub", "AR");
  // getMockCoverageReportDataFromUrl requires dev mode + these two params.
  urlParams.set("idm", "true");
  urlParams.set("isCoverageMock", "true");
  urlParams.set("coverageScenario", coverageScenario);
  if (recommendedMedTier) {
    urlParams.set("recommendedMedTier", recommendedMedTier);
  } else {
    urlParams.delete("recommendedMedTier");
  }

  // Hard navigation (full page load) so the server resolves route=insurance-checker
  // into the meristem context; isInsuranceCheckerRoute() then drives
  // getEligibleMedProducts() correctly. Survey answers persist server-side.
  window.location.href = `${window.location.origin}/insurance-checker/coverageReport?${urlParams}`;
};

export const redirectToMedPlanReveal = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
}) => {
  const isMed = isMedPlanType(planType);
  const medSku = getMedSkuFromPlanType(planType);
  const noomPlanId = getMedPlanIdBySKU(medSku);

  await prepareMedDataAndUrl({
    email,
    isEmailUser,
    glp1Companion,
    planType,
  });

  await getStore().dispatch(
    setRecommendedPlanById(noomPlanId, {
      overwriteForced: true,
      overwriteReset: true,
    })
  );

  const flow = isMed ? MED_FLOW.CLINICAL : MED_FLOW.HRT;

  await getStore().dispatch(
    setRecommendationContext({
      recommendedMedTier: medSku,
      eligibleMedTiers: [medSku],
      flow,
      recommendationId: undefined,
      selectedMedPlan: medSku,
    })
  );

  const entryPoint = medSkuEntryPoints[medSku] ?? "planReveal";
  const flowName = isMed ? "clinical" : "hrt";

  goto.layer("payment-results", null, false, flowName, entryPoint);
};

export async function setHRTPreCheckoutData({ email }: { email: string }) {
  const data = getPayloadForMhtUser(email);
  await getStore().dispatch(
    saveMainSurveyAnswers(data.personaSurveyAnswers, {
      surveyNameSpace: "personaSurveyUS",
    })
  );

  await getStore().dispatch(
    setPurchaseSurveyAnswers(data.paymentSurveyAnswers)
  );

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("fcc", "US");
  urlParams.set("fsub", "AR");
  urlParams.set("idm", "true");
  urlParams.set("MhtEnabledOverwrite", "true");
  urlParams.set("enableMhtFlow", "true");

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);
}

export const redirectToNWCheckoutWithMHTUser = async ({
  email,
  selectedNoomPlanId,
}: {
  email: string;
  selectedNoomPlanId: string;
}) => {
  await setHRTPreCheckoutData({ email });

  goto.checkout(selectedNoomPlanId);
};

export const redirectToHRTCheckoutPage = async ({
  selectedNoomPlanId,
  planType = "cream",
}: {
  planType?: "cream" | "patch";
  selectedNoomPlanId: string;
}) => {
  const { hrtSurveyAnswers } = getPayloadForHRTCheckout({ planType });

  await getStore().dispatch(setHRTSurveyAnswers(hrtSurveyAnswers));

  const creamRequested = planType === "cream";

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("selectHRTCreamPlan", creamRequested.toString());

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);

  goto.hrtCheckout(selectedNoomPlanId);
};

type HealthCheckNavigationParams = {
  email: string;
  planType: HealthPlan;
  bodyGoal: BodyGoalType;
};

const prepareHealthCheckData = async ({
  email,
  planType,
  bodyGoal,
}: HealthCheckNavigationParams) => {
  const data = getPayloadForHealthCheckUser(email, planType, bodyGoal);

  // Initialize survey answers in currentAnswers to mimic ON_START_SURVEY action
  const store = getStore();
  store.dispatch(
    seedCurrentAnswers({
      namespace: "mainSurvey",
      answers: data.surveyAnswers,
    })
  );

  const state = store.getState();
  const surveyAnswers =
    state.currentAnswers.questionAnswers.mainSurvey || data.surveyAnswers;
  await store.dispatch(saveMainSurveyAnswers(surveyAnswers, data.params));

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("route", "health");
  urlParams.set("fcc", "US");
  urlParams.set("fsub", "NY");

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);
};

const isMedSku = (planType: HealthPlan): planType is MED_SKU => {
  return Object.values(MED_SKU).includes(planType as MED_SKU);
};

const setHealthCheckRecommendationContext = async (planType: HealthPlan) => {
  if (isMedSku(planType)) {
    const meristemContext = getRequestMetadata();
    if (meristemContext?.context) {
      meristemContext.context.route_id = routeConstants.health;
    }

    const eligibleMedProducts = getEligibleMedProducts();
    const eligibleMedTiers = getEligibleMedProductsPlanSelection(
      planType,
      eligibleMedProducts
    );

    await getStore().dispatch(
      setRecommendationContext({
        recommendedMedTier: planType,
        eligibleMedTiers,
        flow: MED_FLOW.HEALTH,
        recommendedHealthPlan: planType,
        selectedMedPlan: planType,
      })
    );
  } else {
    await getStore().dispatch(
      setRecommendationContext({
        recommendedHealthPlan: planType,
        selectedMedPlan: planType,
        eligibleMedTiers: [],
      })
    );
  }
};

export const redirectToHealthCheckReport = async ({
  email,
  planType,
  bodyGoal,
}: HealthCheckNavigationParams) => {
  await prepareHealthCheckData({ email, planType, bodyGoal });
  await setHealthCheckRecommendationContext(planType);

  goto.layer(
    "health-payment-results",
    null,
    false,
    "health",
    "healthCheckReport"
  );
};

export const redirectToHealthCheckPlanReveal = async ({
  email,
  planType,
  bodyGoal,
}: HealthCheckNavigationParams) => {
  await prepareHealthCheckData({ email, planType, bodyGoal });
  await setHealthCheckRecommendationContext(planType);

  goto.layer(
    "health-payment-results",
    null,
    false,
    "health",
    "healthCheckResults"
  );
};

export const redirectToHealthCheckCheckout = async ({
  email,
  planType,
  bodyGoal,
}: HealthCheckNavigationParams) => {
  await prepareHealthCheckData({ email, planType, bodyGoal });

  if (isMedSku(planType)) {
    const noomPlanId = getMedPlanIdBySKU(planType);

    await getStore().dispatch(
      setRecommendedPlanById(noomPlanId, {
        overwriteForced: true,
        overwriteReset: true,
      })
    );

    await getStore().dispatch(
      setRecommendationContext({
        recommendedMedTier: planType,
        eligibleMedTiers: [planType],
        flow: MED_FLOW.HEALTH,
        recommendedHealthPlan: planType,
        selectedMedPlan: planType,
      })
    );

    const selectedMedPlanType = getSelectedMedPlanTypeByPlanId(noomPlanId);
    if (selectedMedPlanType) {
      updateSessionState("browser", {
        selectedMedPlanType,
      });
    }

    goto.checkout(noomPlanId, true);
  } else if (planType === NON_MED_PLAN.NOOM_WEIGHT) {
    await getStore().dispatch(
      setRecommendationContext({
        recommendedHealthPlan: planType,
        selectedMedPlan: planType,
      })
    );

    updateSessionState("browser", {
      selectedNoomWeightPlanOption: true,
    });

    goto.checkout("option_n_20221201_Ex1084var1", true);
  } else {
    // TODO: For Freemium plan, consider redirecting to account creation page
    await getStore().dispatch(
      setRecommendationContext({
        recommendedHealthPlan: planType,
        selectedMedPlan: planType,
        eligibleMedTiers: [],
      })
    );

    goto.layer(
      "health-payment-results",
      null,
      false,
      "health",
      "healthCheckResults"
    );
  }
};

const callCoachServerOauth = async (requestData) => {
  const CS_STAGING_OAUTH_URL =
    "https://data-test.noom.com/servlets/oauth/token";
  const requestBody = `${encodeURIComponent("grant_type")}=${encodeURIComponent(
    "password"
  )}&${encodeURIComponent("client_id")}=${encodeURIComponent(
    "noom-coach"
  )}&${encodeURIComponent("request")}=${encodeURIComponent(requestData)}`;

  const requestInit: RequestInit = {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
    body: requestBody,
  };
  return fetch(CS_STAGING_OAUTH_URL, requestInit).then(
    async (response) => {
      const text = await response.text();
      let data: JsonObject | string = text;

      try {
        data = JSON.parse(data);
      } catch (e) {
        /* NOP */
      }

      if (response.ok) {
        await setInAppAutoCookie(
          data.accessCode,
          data.access_token,
          data.accountEmail,
          false
        );
        return data;
      }

      throw new Error("Failed response");
    },
    (err) => {
      throw new Error(err);
    }
  );
};

export const createNewUser = async (email, password) => {
  const payload = {
    action: "create_account",
    email,
    userProfileData: {
      email,
      name: "Noom User",
      password,
      phoneNumber: "1234567890",
      language: "en",
      gender: "FEMALE",
      heightInCm: 180,
      weightInKg: 70,
      age: 30,
      isNursing: false,
    },
    password,
  };

  return send("POST", "/api/enrollment/v2/create_account/", payload);
};

export const authenticateUser = async (email, password) => {
  const requestData = `{"mode":"LOGIN","type":"EMAIL_AND_PASSWORD","principal":"${email}","credentials":"${password}"}`;
  return callCoachServerOauth(requestData);
};

export const getInAppUrl = async (
  email: string,
  accessCode: string,
  accessToken: string,
  countryCode: string,
  isAndroid = false,
  isAppEmail = false,
  campaignName = "",
  route = ""
) => {
  function getUrlEndpointRequest() {
    const headers = {
      Authorization: `bearer ${accessToken}`,
    };

    if (isAppEmail) {
      return {
        path: "/internal/sendAppEmail/",
        headers,
        body: {
          access_code: accessCode,
          country: countryCode,
          email,
        },
      };
    }
    return {
      path: "/internal/v2/app/",
      headers,
      body: {
        user_data: {
          gender: "female",
          email,
          country_code: countryCode,
          access_code: accessCode,
          name: "SM Test",
        },
        route_data: {
          trial_duration_days: 14,
          allow_purchase: isAndroid ? "true" : "false",
          sp: "sp",
          utm_source: "appstore",
          fcc: "en",
          ...(campaignName.length > 0 ? { campaign_name: campaignName } : {}),
        },
      },
    };
  }

  const { path, headers, body } = getUrlEndpointRequest();

  return send("POST", path, body, { headers }).then((res) => {
    // Add protocol if missing
    const baseUrl = res.redirect_url || res.url;
    const protocol = baseUrl.includes("http") ? "" : "http://";
    const url = new URL(protocol + baseUrl);

    // On local env the domain/port of the redirect URL needs to be updated to stay on local env
    if (window.location.origin.includes("localhost")) {
      url.protocol = "http";
      url.hostname = "localhost";
      url.port = "8080";
    }

    // Add route as query parameter if provided
    if (route) {
      url.searchParams.set("route", route);
    }

    return url.href;
  });
};

export const useGoToInApp = (
  setStatus: (status: string) => void,
  email: string,
  password: string
) => {
  const [isAndroid, setIsAndroid] = useState(false);
  const toggleIsAndroid = () => setIsAndroid(!isAndroid);

  const [appEmailUrl, setAppEmailUrl] = useState("");

  const [countryCode, setCountryCode] = useState("US");
  const onCountryCodeChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCountryCode(e.target.value);

  async function createInAppUrl(appEmail, campaignName = "", route = "") {
    const autologinCookie = getInAppAutoCookie();

    let user = {
      accountEmail: autologinCookie?.email,
      accessCode: autologinCookie?.accessCode,
      access_token: autologinCookie?.accessToken,
    };

    if (!autologinCookie) {
      setStatus("Creating new user...");
      await createNewUser(email, password);
      user = await authenticateUser(email, password);
    }

    setStatus("Fetching in-app URL from growth...");

    return getInAppUrl(
      user.accountEmail,
      user.accessCode,
      user.access_token,
      countryCode,
      isAndroid,
      appEmail,
      campaignName,
      route
    );
  }

  async function goToInApp() {
    try {
      const url = await createInAppUrl(false);
      setStatus("Redirecting to in-app...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function goToAppFree() {
    try {
      const url = await createInAppUrl(false, "freemium_test_campaign");
      setStatus("Redirecting to app-free route...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function goToFreemium() {
    try {
      const url = await createInAppUrl(false, "", "app-free-onboarding");
      setStatus("Redirecting to freemium route...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function fetchAppEmailUrl() {
    try {
      const url = await createInAppUrl(true);
      setAppEmailUrl(url);
      setStatus("");
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  function copyUrlToClipboard() {
    navigator.clipboard.writeText(appEmailUrl);
  }

  return {
    goToInApp: useCallbackRef(goToInApp),
    goToAppFree: useCallbackRef(goToAppFree),
    goToFreemium: useCallbackRef(goToFreemium),
    fetchAppEmailUrl: useCallbackRef(fetchAppEmailUrl),
    copyUrlToClipboard,
    isAndroid,
    appEmailUrl,
    toggleIsAndroid,
    countryCode,
    onCountryCodeChange,
  };
};
